import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from './components/ui/sonner';
import { ConfirmProvider } from './components/ConfirmDialog';
import LoginPage from './pages/LoginPage';
import DemoLoginPage from './demo/DemoLoginPage';
import InstallPWA from './components/InstallPWA';
import NotificationPopups from './components/NotificationPopups';
import './App.css';

// Code-split: every route below loads on demand, keeping the initial bundle small for fast first load
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const ParentLoginPage = lazy(() => import('./pages/ParentLoginPage'));
const ParentDashboard = lazy(() => import('./pages/ParentDashboard'));
const SwimHub = lazy(() => import('./pages/SwimHub'));
const DashboardLayout = lazy(() => import('./layouts/DashboardLayout'));
const DashboardOverview = lazy(() => import('./pages/DashboardOverview'));
const StudentsPage = lazy(() => import('./pages/StudentsPage'));
const CourseManagementPage = lazy(() => import('./pages/CourseManagementPage'));
const CertificatesPage = lazy(() => import('./pages/CertificatesPage'));
const StudentProfilePage = lazy(() => import('./pages/StudentProfilePage'));
const EditStudentPage = lazy(() => import('./pages/EditStudentPage'));
const UserManagementPage = lazy(() => import('./pages/UserManagementPage'));
const BatchesPage = lazy(() => import('./pages/BatchesPage'));
const AttendancePage = lazy(() => import('./pages/AttendancePage'));
const FaceAttendancePage = lazy(() => import('./pages/FaceAttendancePage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const ChatPage = lazy(() => import('./pages/ChatPage'));
const FeesManagementPage = lazy(() => import('./pages/FeesManagementPage'));
// Super Admin Pages
const SuperAdminDashboard = lazy(() => import('./pages/SuperAdminDashboard'));
const FeatureManager = lazy(() => import('./pages/admin/FeatureManager'));
const AdminStudentManagement = lazy(() => import('./pages/admin/StudentManagement'));
const AdminAttendanceControl = lazy(() => import('./pages/admin/AttendanceControl'));
const AdminFeeManagement = lazy(() => import('./pages/admin/FeeManagement'));
const WhatsAppPanel = lazy(() => import('./pages/admin/WhatsAppPanel'));
const CourseManagement = lazy(() => import('./pages/admin/CourseManagement'));
const ReportsDashboard = lazy(() => import('./pages/admin/ReportsDashboard'));
const SystemSettings = lazy(() => import('./pages/admin/SystemSettings'));
const CustomizeLayout = lazy(() => import('./pages/admin/CustomizeLayout'));
const HolidaysManagement = lazy(() => import('./pages/admin/HolidaysManagement'));
const PaymentResetPage = lazy(() => import('./pages/admin/PaymentResetPage'));
// CRM Pages
const LeadDashboard = lazy(() => import('./pages/crm/LeadDashboard'));
const LeadsListPage = lazy(() => import('./pages/crm/LeadsListPage'));
const LeadFormPage = lazy(() => import('./pages/crm/LeadFormPage'));
const LeadDetailPage = lazy(() => import('./pages/crm/LeadDetailPage'));
const FollowUpsPage = lazy(() => import('./pages/crm/FollowUpsPage'));
const CRMTrialsPage = lazy(() => import('./pages/crm/CRMTrialsPage'));
const StaffPerformancePage = lazy(() => import('./pages/crm/StaffPerformancePage'));
const IntegrationsPage = lazy(() => import('./pages/crm/IntegrationsPage'));
const BulkMessagesPage = lazy(() => import('./pages/BulkMessagesPage'));
const StaffPayrollPage = lazy(() => import('./pages/StaffPayrollPage'));
const StaffManagementPage = lazy(() => import('./pages/StaffManagementPage'));
const MyAttendancePage = lazy(() => import('./pages/MyAttendancePage'));
const RaceTimingPage = lazy(() => import('./pages/RaceTimingPage'));
const LivePoolPage = lazy(() => import('./pages/LivePoolEntry'));
const MyTeamPage = lazy(() => import('./pages/MyTeamPage'));
const ParentLivePool = lazy(() => import('./pages/ParentLivePool'));
// Employee Attendance Tracker DEMO (isolated SaaS sandbox)
const ManageTrialsPage = lazy(() => import('./pages/admin/ManageTrialsPage'));

const PageLoader = () => (
  <div className="flex items-center justify-center h-screen w-full" style={{ background: 'var(--background, #f8fafc)' }}>
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary"></div>
      </div>
    );
  }

  return user ? children : <Navigate to="/" />;
}

function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <ConfirmProvider>
        <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<LoginPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/parent/login" element={<ParentLoginPage />} />
          <Route path="/parent/dashboard" element={<ParentDashboard />} />
          <Route path="/parent/swim" element={<SwimHub />} />
          <Route path="/parent/live" element={<ParentLivePool />} />
          <Route path="/parent/skill-passport" element={<ParentLivePool />} />
          {/* Employee Attendance Tracker trial login portal (email + password) */}
          <Route path="/demo/login" element={<DemoLoginPage />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardOverview />} />
            <Route path="students" element={<StudentsPage />} />
            <Route path="students/:studentId" element={<StudentProfilePage />} />
            <Route path="students/:studentId/edit" element={<EditStudentPage />} />
            <Route path="batches" element={<BatchesPage />} />
            <Route path="attendance" element={<AttendancePage />} />
            <Route path="face-attendance" element={<FaceAttendancePage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="chat" element={<ChatPage />} />
            <Route path="fees" element={<FeesManagementPage />} />
            <Route path="swim-courses" element={<CourseManagementPage />} />
            <Route path="certificates" element={<CertificatesPage />} />
            <Route path="users" element={<UserManagementPage />} />
            {/* Super Admin Routes */}
            <Route path="admin" element={<SuperAdminDashboard />} />
            <Route path="admin/features" element={<FeatureManager />} />
            <Route path="admin/students" element={<AdminStudentManagement />} />
            <Route path="admin/attendance" element={<AdminAttendanceControl />} />
            <Route path="admin/fees" element={<AdminFeeManagement />} />
            <Route path="admin/whatsapp" element={<WhatsAppPanel />} />
            <Route path="admin/courses" element={<CourseManagement />} />
            <Route path="admin/reports" element={<ReportsDashboard />} />
            <Route path="admin/settings" element={<SystemSettings />} />
            <Route path="admin/customize" element={<CustomizeLayout />} />
            <Route path="admin/holidays" element={<HolidaysManagement />} />
            <Route path="admin/payment-reset" element={<PaymentResetPage />} />
            <Route path="admin/trials" element={<ManageTrialsPage />} />
            {/* CRM Routes */}
            <Route path="lead-dashboard" element={<LeadDashboard />} />
            <Route path="leads" element={<LeadsListPage />} />
            <Route path="leads/new" element={<LeadFormPage />} />
            <Route path="leads/:id" element={<LeadDetailPage />} />
            <Route path="leads/:id/edit" element={<LeadFormPage />} />
            <Route path="follow-ups" element={<FollowUpsPage />} />
            <Route path="crm-trials" element={<CRMTrialsPage />} />
            <Route path="staff-performance" element={<StaffPerformancePage />} />
            <Route path="integrations" element={<IntegrationsPage />} />
            <Route path="bulk-messages" element={<BulkMessagesPage />} />
            <Route path="staff-payroll" element={<StaffPayrollPage />} />
            <Route path="staff-management" element={<StaffManagementPage />} />
            <Route path="race-timing" element={<RaceTimingPage />} />
            <Route path="live-pool" element={<LivePoolPage />} />
            <Route path="my-team" element={<MyTeamPage />} />
            <Route path="skill-passport" element={<LivePoolPage />} />
            <Route path="my-attendance" element={<MyAttendancePage />} />
          </Route>
        </Routes>
        </Suspense>
      </BrowserRouter>
      <InstallPWA />
      <NotificationPopups />
      <Toaster position="top-right" richColors closeButton visibleToasts={5} />
      </ConfirmProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
